import {
  IBeneficiarie,
  IGetBeneficiariesRequest,
} from '@smiles/voebiz-core-beneficiaries/dist/Types/BeneficiariesApi.types';
import React, { FC, createContext, useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import { getBeneficiariesList } from '../../services/services';
import { sleep } from '../../utils/sleep';

const beneficiariesDefault = {
  beneficiaries: [] as IBeneficiarie[],
  loading: false,
  isEmpty: false,
  isLastPage: false,
  isEmptySearch: false,
  isEmptyFilter: false,
  setFilter: () => undefined,
  showMore: () => undefined,
  selectedPage: (page: number) => undefined,
  cpf: '',
  setCpf: () => undefined,
};

export interface ContextBeneficiary {
  beneficiaries: IBeneficiarie[] | undefined;
  loading: boolean;
  isEmpty: boolean;
  currentPage: number;
  totalPages: number;
  isLastPage: boolean;
  isEmptyFilter: boolean;
  isEmptySearch: boolean;
  filter?: IGetBeneficiariesRequest;
  setFilter: (filter: IGetBeneficiariesRequest) => void;
  showMore: () => void;
  selectedPage: (page: number) => void;
  getListBeneficiaries?: () => void;
  cpf: string;
  setCpf: (cpf: string) => void;
}

const BeneficiariesContext = createContext<Partial<ContextBeneficiary>>(beneficiariesDefault);

const BeneficiariesProvider: FC<{ children: JSX.Element }> = ({ children }) => {
  const [cpf, setCpf] = useState('');

  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();

  const pageUrl = searchParams.get('p');

  const urlCpf = searchParams.get('cpf');

  const isBeneficiariesList = pageUrl === null;

  const [isLastPage, setIsLastPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [beneficiaries, setBeneficiaries] = useState<IBeneficiarie[]>();

  const [filter, setFilterState] = useState<IGetBeneficiariesRequest>({
    page: 1,
    pageSize: 25,
  });

  const lastFilterRef = useRef<IGetBeneficiariesRequest>();

  const isExtractPage = useCallback(async () => {
    if (pageUrl === 'extrato-beneficiario' || pageUrl === 'perfil-colaborador') {
      await sleep(1000);

      return window.postMessage({ type: 'URL_CHANGE', isExtractPage: true, cpf: cpf || urlCpf }, '*');
    }

    return window.postMessage({ type: 'URL_CHANGE', isExtractPage: false, cpf: '' }, '*');
  }, [pageUrl, cpf, urlCpf]);

  const setFilter = useCallback((newFilter: IGetBeneficiariesRequest) => {
    setFilterState((prevFilter) => ({ ...prevFilter, ...newFilter }));
  }, []);

  const selectedPage = useCallback(
    (page: number) => {
      const pageSize = 25;

      setFilter({ page, pageSize });
    },
    [filter.page, setFilter],
  );

  const getListBeneficiaries = useCallback(() => {
    lastFilterRef.current = filter;
    setLoading(true);
    getBeneficiariesList(filter)
      .then(({ data }) => {
        const response = data as {
          beneficiaryList: IBeneficiarie[];
          pageSize: number;
          currentPage: number;
          totalPages: number;
          lastPage: boolean;
        };

        if (response) {
          setCurrentPage(response?.currentPage);
          setTotalPages(response?.totalPages);
          setIsLastPage(response?.lastPage);
          setBeneficiaries(response?.beneficiaryList);
        } else setBeneficiaries([]);
      })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    if (!isEqual(filter, lastFilterRef.current)) {
      isBeneficiariesList && getListBeneficiaries();
    }
  }, [filter, getListBeneficiaries, isBeneficiariesList]);

  useEffect(() => {
    isExtractPage();
  }, [isExtractPage]);

  const isEmpty = !beneficiaries || beneficiaries.length === 0;

  const avaliableFilters = {
    status: filter?.status,
    createdStart: filter?.createdStart,
    createdEnd: filter?.createdEnd,
    birthStart: filter?.birthStart,
    birthEnd: filter?.birthEnd,
  };

  const isEmptyFilter = !Object.values({ ...avaliableFilters }).some((value) => value);

  const isEmptySearch = !filter?.search;

  const value = useMemo(
    () => ({
      setFilter,
      selectedPage,
      beneficiaries,
      loading,
      filter,
      isLastPage,
      currentPage,
      totalPages,
      isEmpty,
      isEmptyFilter,
      isEmptySearch,
      getListBeneficiaries,
      cpf,
      setCpf,
    }),
    [
      setFilter,
      selectedPage,
      beneficiaries,
      loading,
      filter,
      isLastPage,
      currentPage,
      totalPages,
      isEmpty,
      isEmptyFilter,
      isEmptySearch,
      getListBeneficiaries,
      cpf,
    ],
  );

  return <BeneficiariesContext.Provider value={value}>{children}</BeneficiariesContext.Provider>;
};

const useBeneficiaries = () => {
  const beneficiaries = useContext(BeneficiariesContext);

  if (!beneficiaries) {
    throw new Error('Cannot use `useBeneficiaries` outside of BeneficiariesProvider');
  }
  return beneficiaries;
};

export { BeneficiariesContext, BeneficiariesProvider, useBeneficiaries };
